<template>
  <div v-if="additional">
    <h2>Additional</h2>
    <v-btn :to="{ name: 'additionals' }">
      <v-icon>chevron_left</v-icon>Additionals
    </v-btn>
    <p>
      Add sections which will appear at the bottom of a quotation. Toggle
      categories for which the section will appear. Add paragraphs and lists and
      edit them to build the section.
    </p>
    {{ selectedIndex }}
    <table border="1">
      <tr>
        <th>Id</th>
        <td>{{ additional.id }}</td>
      </tr>
      <tr>
        <th>Date</th>
        <td>{{ additional.timestamp | formatDateTime }}</td>
      </tr>
      <tr>
        <th>Status</th>
        <td>
          <v-switch
            :label="locked ? 'Unpublished' : 'Published'"
            v-model="locked"
            @change="updateAdditionalLocked"
          />
        </td>
      </tr>
    </table>
    <v-btn v-if="additional.locked" @click="addAdditionalSection">
      <v-icon>add</v-icon>Section
    </v-btn>
    <div
      class="additionalSection"
      v-for="(section, asindex) in additional.data.sections"
      :key="generateIndex(section, null, null)"
    >
      <div style="float: right" v-if="additional.locked">
        <v-btn @click="changeIndex(section, null, null)" small>
          <v-icon>edit</v-icon>Edit
        </v-btn>
        <v-btn @click="moveSection(section, true)" v-if="asindex != 0" small
          >Up</v-btn
        >
        <v-btn
          @click="moveSection(section, false)"
          v-if="asindex < additional.data.sections.length - 1"
          small
          >Down</v-btn
        >
        <v-btn @click="addAdditionalBlock(section, 'paragraph')" small>
          <v-icon>add</v-icon>Paragraph
        </v-btn>
        <v-btn @click="addAdditionalBlock(section, 'list')" small>
          <v-icon>add</v-icon>List
        </v-btn>
        <v-btn @click="removeAdditionalSection(section)" color="error" small>
          <v-icon>delete</v-icon>Delete
        </v-btn>
      </div>
      <div
        class="ajaxForm"
        v-if="generateIndex(section, null, null) == selectedIndex"
      >
        <v-textarea label="Title" v-model="genericStringWrapper.value" />
        <v-btn @click="updateAdditionalSectionTitle(section)">Edit</v-btn>
      </div>
      <h1 v-if="generateIndex(section, null, null) != selectedIndex">
        {{ section.title }}
      </h1>
      <div
        v-for="block in section.blocks"
        :key="generateIndex(section, block, null)"
      >
        <template v-if="block.type == 'paragraph'">
          <div
            class="ajaxForm"
            v-if="generateIndex(section, block, null) == selectedIndex"
          >
            <v-textarea label="Value" v-model="genericStringWrapper.value" />
            <v-btn @click="updateAdditionalParagraph(section, block)"
              >Edit</v-btn
            >
          </div>
          <div v-if="generateIndex(section, block, null) != selectedIndex">
            {{ block.value }}
            <v-btn
              @click="changeIndex(section, block, null)"
              v-if="additional.locked"
              small
            >
              <v-icon>edit</v-icon>Edit
            </v-btn>
            <v-btn
              @click="removeAdditionalBlock(section, block)"
              v-if="additional.locked"
              color="error"
              small
            >
              <v-icon>delete</v-icon>Delete
            </v-btn>
          </div>
        </template>
        <div v-if="block.type == 'list'">
          <v-btn
            @click="addAdditionalListItem(section, block)"
            v-if="additional.locked"
            small
          >
            <v-icon>add</v-icon>List item
          </v-btn>
          <v-btn
            @click="removeAdditionalBlock(section, block)"
            v-if="additional.locked"
            color="error"
            small
          >
            <v-icon>delete</v-icon>Delete
          </v-btn>
          <ul>
            <li
              v-for="item in block.items"
              :key="generateIndex(section, block, item)"
            >
              <div
                class="ajaxForm"
                v-if="generateIndex(section, block, item) == selectedIndex"
              >
                <v-textarea
                  label="Value"
                  v-model="genericStringWrapper.value"
                />
                <v-btn @click="updateAdditionalListItem(section, block, item)"
                  >Edit</v-btn
                >
              </div>
              <div v-if="generateIndex(section, block, item) != selectedIndex">
                {{ item.value }}
                <v-btn
                  @click="changeIndex(section, block, item)"
                  v-if="additional.locked"
                  small
                  >edit</v-btn
                >
                <v-btn
                  @click="removeAdditionalListItem(section, block, item)"
                  v-if="additional.locked && block.items.length > 1"
                  color="error"
                  small
                >
                  <v-icon>delete</v-icon>Delete
                </v-btn>
              </div>
            </li>
          </ul>
        </div>
      </div>
      <p>
        <span
          v-for="(quoteType, asqtindex) in section.quoteTypes"
          :key="`section${asindex}asqt${asqtindex}`"
        >
          <span>{{ quoteType.name }}</span>
          <template v-if="quoteType.omit">
            <v-btn
              @click="updateSectionQuoteTypeOmit(section, quoteType.id, false)"
              v-if="additional.locked"
              >Visible</v-btn
            >
            <span class="quoteTypeHidden">&#160;Hidden</span>
          </template>
          <template v-if="!quoteType.omit">
            <v-btn
              @click="updateSectionQuoteTypeOmit(section, quoteType, true)"
              v-if="additional.locked"
              >Hidden</v-btn
            >
            <span class="quoteTypeVisible">&#160;Visible</span>
          </template>
          <span v-if="asqtindex < section.quoteTypes.length - 1">&#160;</span>
        </span>
      </p>
    </div>
  </div>
</template>

<script>
import nbmlaw from "@/api/nbmlaw";
export default {
  name: "Additional",
  metaInfo() {
    return {
      title: "Additional",
      titleTemplate: "%s - Sales - Conveyancing Tracker",
      htmlAttrs: {
        lang: "en",
        amp: true
      }
    };
  },
  data() {
    return {
      additional: null,
      quoteTypes: null,
      selectedIndex: null,
      genericStringWrapper: null,
      locked: false
    };
  },
  methods: {
    async fetchAdditional() {
      const response = await nbmlaw.get(
        `/cors/admin/sales/additionals/${this.$route.params.id}`
      );
      // console.log(response.data);
      this.additional = response.data;
    },
    async fetchQuoteTypes() {
      const response = await nbmlaw.get(`/cors/admin/sales/quoteTypes`);
      // console.log(response.data);
      this.quoteTypes = response.data;
    },
    changeIndex(section, block, item) {
      this.selectedIndex = this.generateIndex(section, block, item);
      if (item != undefined) {
        this.genericStringWrapper = {
          value: this.additional.data.sections
            .find((s) => s.id === section.id)
            .blocks.find((b) => b.id === block.id)
            .items.find((i) => i.id === item.id).value
        };
      } else if (block != undefined) {
        this.genericStringWrapper = {
          value: this.additional.data.sections
            .find((s) => s.id === section.id)
            .blocks.find((b) => b.id === block.id).value
        };
      } else if (section != undefined) {
        this.genericStringWrapper = {
          value: this.additional.data.sections.find((s) => s.id === section.id)
            .title
        };
      } else {
        this.genericStringWrapper = null;
      }
    },
    generateIndex(section, block, item) {
      if (item != undefined) {
        return `section${section.id}block${block.id}listitem${item.id}`;
      } else if (block != undefined) {
        return `section${section.id}block${block.id}`;
      } else if (section != undefined) {
        return `section${section.id}`;
      }
      return null;
    },
    async moveSection(section, up) {
      await nbmlaw.put(
        `/cors/admin/sales/additionals/${this.$route.params.id}/sections/${section.id}/move`,
        null,
        {
          params: { up }
        }
      );
      // console.log(response.data);
      this.fetchAdditional();
    },
    async addAdditionalSection() {
      await nbmlaw.post(
        `/cors/admin/sales/additionals/${this.$route.params.id}/sections`,
        null
      );
      // console.log(response.data);
      this.fetchAdditional();
    },
    async addAdditionalBlock(section, type) {
      await nbmlaw.post(
        `/cors/admin/sales/additionals/${this.$route.params.id}/sections/${section.id}/blocks`,
        null,
        { params: { type } }
      );
      // console.log(response.data);
      this.fetchAdditional();
    },
    async addAdditionalListItem(section, block) {
      await nbmlaw.post(
        `/cors/admin/sales/additionals/${this.$route.params.id}/sections/${section.id}/blocks/${block.id}/listitems`,
        null
      );
      // console.log(response.data);
      this.fetchAdditional();
    },
    async updateAdditionalLocked() {
      await nbmlaw.put(
        `/cors/admin/sales/additionals/${this.$route.params.id}/locked`,
        { locked: this.locked }
      );
      // console.log(response.data);
      this.fetchAdditional();
    },
    async updateAdditionalSectionTitle(section) {
      await nbmlaw.put(
        `/cors/admin/sales/additionals/${this.$route.params.id}/sections/${section.id}/title`,
        this.genericStringWrapper
      );
      // console.log(response.data);
      this.fetchAdditional();
      this.changeIndex(null, null, null);
    },
    async updateAdditionalParagraph(section, block) {
      await nbmlaw.put(
        `/cors/admin/sales/additionals/${this.$route.params.id}/sections/${section.id}/blocks/${block.id}/value`,
        this.genericStringWrapper
      );
      // console.log(response.data);
      this.fetchAdditional();
      this.changeIndex(null, null, null);
    },
    async updateAdditionalListItem(section, block, item) {
      await nbmlaw.put(
        `/cors/admin/sales/additionals/${this.$route.params.id}/sections/${section.id}/blocks/${block.id}/listitems/${item.id}/value`,
        this.genericStringWrapper
      );
      // console.log(response.data);
      this.fetchAdditional();
      this.changeIndex(null, null, null);
    },
    async removeAdditionalSection(section) {
      if (confirm("Are you sure?")) {
        await nbmlaw.delete(
          `/cors/admin/sales/additionals/${this.$route.params.id}/sections/${section.id}`
        );
        // console.log(response.data);
        this.fetchAdditional();
      }
    },
    async removeAdditionalBlock(section, block) {
      if (confirm("Are you sure?")) {
        await nbmlaw.delete(
          `/cors/admin/sales/additionals/${this.$route.params.id}/sections/${section.id}/blocks/${block.id}`
        );
        // console.log(response.data);
        this.fetchAdditional();
      }
    },
    async removeAdditionalListItem(section, block, item) {
      if (confirm("Are you sure?")) {
        await nbmlaw.delete(
          `/cors/admin/sales/additionals/${this.$route.params.id}/sections/${section.id}/blocks/${block.id}/listitems/${item.id}`
        );
        // console.log(response.data);
        this.fetchAdditional();
      }
    },
    async updateSectionQuoteTypeOmit(section, quotetypeid, value) {
      await nbmlaw.put(
        `/cors/admin/sales/additionals/${this.$route.params.id}/sections/${section.id}/quotetypes/${quotetypeid}/omit`,
        null,
        {
          params: { value }
        }
      );
      // console.log(response.data);
      this.fetchAdditional();
    }
  },
  watch: {
    "$route.params.id": {
      immediate: true,
      handler() {
        this.fetchAdditional();
      }
    },
    additional: {
      handler() {
        this.locked = this.additional.locked;
      }
    }
  },
  created() {
    this.fetchQuoteTypes();
  }
};
</script>

<style scoped>
.additionalSection {
  padding: 10px;
  border: 2px solid gray;
  margin-bottom: 10px;
}

.quoteTypeHidden {
  color: red;
}

.quoteTypeVisible {
  color: green;
}
</style>